import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router, RoutesRecognized } from '@angular/router';
import { catchError, filter, map, pairwise } from 'rxjs/operators';
import { jwtDecode }from 'jwt-decode';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { usersService } from './users.service';
import { utilityService } from './utility.service';


@Injectable({
    providedIn: 'root'
})
export class authService {
    public userAuthData: any;
    public userLogData: any;
    public isAuthenticated: boolean = false;
    public userToken: any;
    public currentUrl: string;
    previousUrl:any;
    public loginUser: any;
    
    public userDetail = new BehaviorSubject<any>(null);
    userDetail$ = this.userDetail.asObservable();



    constructor(
        private location: Location,
        private router: Router,
        private userService: usersService,
        public utility: utilityService
    ) {
        this.currentUrl = this.router.url;
        this.previousUrl = null;

        this.router.events
            .pipe(filter((event: any) => event instanceof RoutesRecognized), pairwise())
            .subscribe((events: RoutesRecognized[]) => {
                if (events[0].urlAfterRedirects) {
                    this.previousUrl = events[0].urlAfterRedirects;
                }
                if (events[1].urlAfterRedirects) {
                    this.currentUrl = events[1].urlAfterRedirects;

                }
            });

        window.addEventListener('pageshow', (event: PageTransitionEvent) => {
            if (!event.persisted || !this.isProtectedPath(window.location.pathname)) {
                return;
            }

            const token = localStorage.getItem(environment.token_lable);
            if (!token) {
                this.clearAuthState(true);
                return;
            }

            this.validateToken(token).subscribe((isValid) => {
                if (!isValid) {
                    this.clearAuthState(true);
                }
            });
        });
    }

    private isProtectedPath(pathname: string): boolean {
        return pathname === '/user/profile'
            || pathname.startsWith('/user/profile/')
            || pathname === '/user/billing'
            || pathname === '/user/settings'
            || pathname === '/user/dashboard'
            || pathname.startsWith('/user/dashboard/')
            || pathname.startsWith('/coins/');
    }



    getDecodedAccessToken(token: string): any {
        try {
            return jwtDecode(token);
        }
        catch (Error) {
            return null;
        }
    }

    getLoggedInUserData(token: string): void {
        this.userToken = token;
        this.userAuthData = this.getDecodedAccessToken(token);
        const userId = this.userAuthData?.user?.id;
        if (!userId) {
            this.clearAuthState(true);
            return;
        }
        this.getUserDetails(userId);
    }

    getUserDetails(userId: string = this.userAuthData?.user?.id){
        if (!userId) {
            this.clearAuthState(true);
            return;
        }

        this.userService.getUserById(userId).subscribe({
            next: (res) => {
            if(res?.data?.id && res.data.id === userId){
                this.isAuthenticated = true;
                this.userDetail.next(res.data);
                this.utility.updateCoinsIARCounter(userId);
            } else {
                this.clearAuthState(true);
            }
            },
            error: (error) => {
                // A temporary API or database outage must not sign the member
                // out. Authentication failures still clear the local session.
                if (this.isDefinitiveAuthFailure(error)) this.clearAuthState(true);
            }
        });
    }

    public _userLogged: Subject<any> = new Subject<any>(); 
    public userLoggedObs = this._userLogged.asObservable();
    private resetAuthState(): void {
        this._userLogged.next(false);
        localStorage.removeItem(environment.token_lable);
        this.userAuthData = null;
        this.isAuthenticated = false;
        this.userToken = null;
        this.loginUser = null;
        this.userDetail.next(null);
    }

    clearAuthState(navigateToLogin: boolean = false): void {
        this.resetAuthState();
        if (navigateToLogin) {
            this.router.navigate(['/user/login'], { replaceUrl: true });
        }
    }

    handleUnauthorized(): void {
        this.clearAuthState(true);
    }

    logoutUser(): void {
        this.clearAuthState(true);
    }

    refreshUserDetails(): void {
        this.reupdateAppData();
        return
        const userId = this.userAuthData?.user?.id;
        if (!userId) return;

        this.userService.getUserById(userId).subscribe(res => {
            if (res?.data?.id === userId) {
                console.log('res data received' , res.data);
            this.userDetail.next(res.data);
            }
        });
        }

    refreshUserDetailsOnce(): Observable<any> {
        const userId = this.userAuthData?.user?.id;
        if (!userId) return of(null);
        return this.userService.getUserById(userId).pipe(
            map((res: any) => {
                if (res?.data?.id !== userId) return null;
                this.isAuthenticated = true;
                this.userDetail.next(res.data);
                return res.data;
            })
        );
    }


        reupdateAppData(){
            const token =  localStorage.getItem(environment.token_lable);
            if(!token){
            return
            }
            this.getLoggedInUserData(token);
        }



     getLoggedInUserId(){
        return this.userAuthData.user.id;
    }


    validateToken(token: string): Observable<boolean> {
        const decodedToken = this.getDecodedAccessToken(token);
        const userId = decodedToken?.user?.id;

        if (!userId) {
            this.clearAuthState();
            return of(false);
        }

        return this.userService.getUserById(userId).pipe(
            map((res: any) => {
                if (res?.data?.id !== userId) {
                    this.clearAuthState();
                    return false;
                }

                this.userToken = token;
                this.userAuthData = decodedToken;
                this.isAuthenticated = true;
                this.userDetail.next(res.data);
                return true;
            }),
            catchError((error) => {
                if (this.isDefinitiveAuthFailure(error) || !this.isLocallyValidToken(decodedToken)) {
                    this.clearAuthState();
                    return of(false);
                }

                // Keep a locally valid session during transient status-0/5xx
                // failures. Every protected API still verifies the token.
                this.userToken = token;
                this.userAuthData = decodedToken;
                this.isAuthenticated = true;
                return of(true);
            })
        );
    }

    private isDefinitiveAuthFailure(error: any): boolean {
        return [400, 401, 403, 404].includes(Number(error?.status));
    }

    private isLocallyValidToken(decodedToken: any): boolean {
        const expiresAt = Number(decodedToken?.exp || 0) * 1000;
        return Boolean(decodedToken?.user?.id && expiresAt > Date.now());
    }

    personalToken(token: string): Observable<boolean> {
        return this.validateToken(token);
    }

}
